import { Client, Conversation, Message } from '@twilio/conversations';
import type { ChatProvider, ChatState, ChatMessage } from '../types';

type StateCallback = (state: ChatState) => void;

export class TwilioChatProvider implements ChatProvider {
  readonly name = 'twilio';

  private client: Client | null = null;
  private conversation: Conversation | null = null;
  private state: ChatState = {
    isConnected: false,
    isConnecting: false,
    messages: [],
    error: null,
    historyLoaded: false,
  };
  private subscribers: Set<StateCallback> = new Set();
  private localIdentity: string = '';

  getState(): ChatState {
    return { ...this.state };
  }

  subscribe(callback: StateCallback): () => void {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  private notify(): void {
    const state = this.getState();
    this.subscribers.forEach(cb => cb(state));
  }

  private updateState(partial: Partial<ChatState>): void {
    this.state = { ...this.state, ...partial };
    this.notify();
  }

  async connect(token: string, conversationSid: string): Promise<void> {
    this.updateState({ isConnecting: true, error: null });

    try {
      this.client = await Client.create(token);
      this.localIdentity = this.client.user.identity;

      this.conversation = await this.client.getConversationBySid(conversationSid);

      // Load existing messages
      const paginator = await this.conversation.getMessages();
      const messages = paginator.items.map(m => this.wrapMessage(m));

      // historyLoaded flips true in the SAME update that publishes the history
      // batch, and BEFORE the messageAdded listener is attached below — so
      // consumers can treat these messages as the baseline and every later
      // messageAdded as live (IB-004 soft-focus).
      this.updateState({
        isConnected: true,
        isConnecting: false,
        messages,
        historyLoaded: true,
      });

      // Listen for new messages
      this.conversation.on('messageAdded', (message) => {
        this.updateState({
          messages: [...this.state.messages, this.wrapMessage(message)],
        });
      });

    } catch (err) {
      this.updateState({
        isConnecting: false,
        error: err instanceof Error ? err.message : 'Chat connection failed',
      });
      throw err;
    }
  }

  async sendMessage(text: string, attributes?: Record<string, unknown>): Promise<void> {
    if (!this.conversation) return;
    // text can be empty string for rich messages (e.g., product cards)
    if (!text.trim() && !attributes) return;

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await this.conversation.sendMessage(text.trim(), attributes as any);
    } catch (err) {
      this.updateState({
        error: err instanceof Error ? err.message : 'Failed to send message',
      });
      throw err;
    }
  }

  disconnect(): void {
    if (this.client) {
      this.client.shutdown();
      this.client = null;
    }
    this.conversation = null;

    this.updateState({
      isConnected: false,
      messages: [],
      historyLoaded: false,
    });
  }

  private wrapMessage(message: Message): ChatMessage {
    return {
      id: message.sid,
      author: message.author || 'Unknown',
      body: message.body || '',
      timestamp: message.dateCreated || new Date(),
      isLocal: message.author === this.localIdentity,
      attributes: (message.attributes as Record<string, unknown>) ?? undefined,
      attachedMedia: message.attachedMedia ?? undefined,
    };
  }
}
